"use client";

import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import type { ReactElement } from "react";
import { useState } from "react";
import { getQueryClient } from "./query-client";
import { createIdbPersister } from "./query-persister";
import { isMessagesQueryKey } from "./chat/message-cache";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * H2-2125 — the buster for the persisted IndexedDB cache.
 *
 * This used to be `process.env.NEXT_PUBLIC_APP_VERSION ?? "0.1.0"`, and that
 * env var was set NOWHERE: not in scripts/build-web.sh, not in web/Dockerfile,
 * not in any workflow, not in kustomize. `NEXT_PUBLIC_*` is inlined at build
 * time, so every production bundle shipped the constant "0.1.0" and a deploy
 * NEVER invalidated a user's client cache — the 7-day `maxAge` was the only
 * eviction. Shipping a fix for a client-state bug therefore did nothing for
 * anyone already holding the corrupted state, which is how three separate
 * "seed it once and wait for a signal to retire it" markers (H2-603 `clientId`,
 * H2-2021 `optimistic`, H2-1522 one-shot `queued`) turned from same-session
 * cosmetic glitches into week-long, cross-deploy corruption.
 *
 * scripts/build-web.sh now derives this from the git SHA and hard-fails if it
 * cannot, so the value changes on every deploy. The literal below is only ever
 * reached by `next dev`, where a stable string is what you want: a buster that
 * changed per reload would make the persisted cache untestable locally.
 */
const CACHE_BUSTER = process.env.NEXT_PUBLIC_APP_VERSION ?? "dev";

/**
 * H2-2159 — chat transcripts are never written to disk.
 *
 * There was no dehydrate filter at all, so this persister wrote EVERY query
 * namespace into one IndexedDB blob for 7 days — skills, agents, channels,
 * schedules, evals, and the live chat transcript alike. For a list of skills
 * that is a useful warm cache. For a transcript it is actively wrong: the
 * messages query is `refetchOnMount: "always"`, so the disk copy is never the
 * answer, it is only ever a stale flash before the real one lands (H2-1422
 * exists solely to hide that flash). And it is what turned every client-state
 * bug into a WEEK-long one: a bad merge result was written to disk, restored
 * on the next load, and re-rendered wrong until the buster changed — which,
 * before H2-2125, it never did.
 *
 * So chat state stops being durable and goes back to being what it is: a live
 * view of server rows, fetched on mount. Everything else keeps its warm cache;
 * nothing about those namespaces was broken and cold-starting the whole app to
 * fix the chat would be a worse trade.
 */
function shouldPersistQuery(queryKey: readonly unknown[]): boolean {
  return !isMessagesQueryKey(queryKey);
}

const persistOptions = {
  persister: createIdbPersister(),
  maxAge: SEVEN_DAYS_MS,
  buster: CACHE_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) =>
      shouldPersistQuery(query.queryKey),
  },
};

export function Providers({
  children,
}: {
  children: React.ReactNode;
}): ReactElement {
  const [queryClient] = useState(() => getQueryClient());

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      {children}
    </PersistQueryClientProvider>
  );
}
